import { useState, useEffect } from "react";
import { useTRPC } from "@/trpc/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMerchant } from "@/hooks/useMerchant";
import { InvoiceBatch } from "@/types/types";

export function useMutateInvoices(onPollingEnd?: () => void) {
  const { merchant } = useMerchant();
  const trpc = useTRPC();

  const [uploadReference, setUploadReference] = useState<string | null>(null);
  const [invoiceBatch, setInvoiceBatch] = useState<InvoiceBatch | null>(null);

  const getUploadUrl = useMutation({
    ...trpc.invoices.getInvoiceUploadUrl.mutationOptions(),
    onSuccess: (data) => {
      setUploadReference(data.reference);
    },
  });

  const batchQuery = useQuery({
    ...trpc.invoices.getBatchByReference.queryOptions({
      rentalsMerchantId: merchant?.id || "",
      reference: uploadReference || "",
    }),
    enabled: !!uploadReference && !!merchant?.id,
    refetchInterval: (query) => {
      const data = query.state.data as InvoiceBatch | undefined;
      if (data?.status === "complete" || data?.status === "error") {
        return false;
      }
      return 2000; // Poll every 2 seconds until the batch is complete or has an error
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!uploadReference) {
      setInvoiceBatch(null);
      return;
    }
    if (batchQuery.data) {
      setInvoiceBatch(batchQuery.data);
    }
  }, [batchQuery.data, uploadReference]);

  useEffect(() => {
    if (batchQuery.data?.status === "complete") {
      onPollingEnd?.();
    }
  }, [batchQuery.data?.status, onPollingEnd]);

  const resetBatch = () => {
    setInvoiceBatch(null);
    setUploadReference(null);
  };

  return { getUploadUrl, invoiceBatch, resetBatch };
}
